import { assert } from "../asserts/index.ts";
import type { NodeSlotPath, NodeSlotPathSegment, PortableDesignerNodeInContext } from "../schemas/index.ts";

export interface ResolveReferencesByPathOptions {
  importableNode: PortableDesignerNodeInContext;
  destinationSlotPath: NodeSlotPath;
  context: {
    destinationSlotPathByIdMap: Map<number, NodeSlotPath>;
  };
}

// algorithm to find equivalent path in target from the source
// 1. take the path of the destination slot
// 2. take the path of the source slot
// 3. take the source slot path of the referenced node
// 4. find all the slot paths in the destination that match the referenced node's slot paths (ignoring slot indexes)
// 5. if there are multiple matches find the one that matches the destination slot for slot index all the way down (i.e. the lineage)
// 5a. also if there are no lineage matches, look for the paths where the part of the lineage after it matches is unambiguous (not in an array)
// 6. if multiple come back, match the slot paths in reverse for the target slot
// 7. the remaining slot path is the new node id
//
// NOTE: there will be limitations, and it may need to be rewritten with new algorithm in future

/**
 * Nested navgroups:
 * scene -> LU -> NG -> LU -> CA -> LO -> FC -> FC -> Target
 * scene -> LU -> NG -> LU -> NG => LU -> CA -> LO -> FC -> FC -> Target
 */
export const resolveNodeReferencesByPath = ({
  importableNode: { linkages },
  destinationSlotPath,
  context: { destinationSlotPathByIdMap },
}: ResolveReferencesByPathOptions): [number, number | undefined][] => {
  if (!linkages?.externalNodeSlotPaths) {
    return [];
  }

  const destSlotPathsByNodeId = [...destinationSlotPathByIdMap.entries()];

  return linkages.externalNodeSlotPaths.map(([nodeId, slotPath]) => {
    const candidateSlotPathEntries = destSlotPathsByNodeId.filter(entryHasMatchingSlotPathIgnoringIndex(slotPath));

    // entries where the part following the shared lineage does not have multiple values (aka we can be sure there is only one candidate)
    const matchingUnambiguousPathEntries = candidateSlotPathEntries.filter(
      entryHasUnambiguousLineage(destinationSlotPath),
    );

    // entries that match the lineage of the destination slot path
    const matchingLineageEntries = candidateSlotPathEntries.filter(entryMatchesSlotPathLineage(destinationSlotPath));

    // ok now find the ones which end in the referenced node slot
    const targetSlot = slotPath[slotPath.length - 1];
    const matchingEntries = [...matchingLineageEntries, ...matchingUnambiguousPathEntries].filter(
      ([, candidate]) =>
        candidate.length > 0 && segmentsAreEqualIgnoringIndex(candidate[candidate.length - 1], targetSlot),
    );

    if (matchingEntries.length === 0) {
      // typically this would be if the referenced node is ambiguous e.g. a random variable in a variables array pibling/sibling
      // to solve that will require more fancy and user input in some cases
      return [nodeId, undefined];
    }

    // lastly get the longest segment aka the nearest to our destination slot path
    const newNodeId = matchingEntries.sort((a, b) => b[1].length - a[1].length)[0][0];
    return [nodeId, newNodeId];
  });
};

const entryHasMatchingSlotPathIgnoringIndex =
  (subject: NodeSlotPath) =>
  ([, candidate]: [number, NodeSlotPath]) =>
    pathsAreEqualByShortestIgnoringIndex(subject, candidate);

const entryMatchesSlotPathLineage =
  (subject: NodeSlotPath) =>
  ([, candidate]: [number, NodeSlotPath]) =>
    pathsAreEqualByShortest(subject, candidate);

const entryHasUnambiguousLineage =
  (subject: NodeSlotPath) =>
  ([, candidate]: [number, NodeSlotPath]) =>
    isUnambiguousSlotPath(candidate, subject);

const segmentsAreEqualIgnoringIndex = (a: NodeSlotPathSegment, b: NodeSlotPathSegment) => {
  assert(a && b, "Both segments must be defined");
  return a.parent[0] === b.parent[0] && a.name === b.name;
};

const pathsAreEqualByShortestIgnoringIndex = (subject: NodeSlotPath, candidate: NodeSlotPath) => {
  const [big, small] = subject.length < candidate.length ? [candidate, subject] : [subject, candidate];
  return small.every((segment, index) => big[index] && segmentsAreEqualIgnoringIndex(segment, big[index]));
};

const pathsAreEqualByShortest = (subject: NodeSlotPath, candidate: NodeSlotPath) => {
  const [big, small] = subject.length < candidate.length ? [candidate, subject] : [subject, candidate];
  return small.every((segment, index) => segmentsAreEqual(segment, big[index]));
};

const segmentsAreEqual = (a: NodeSlotPathSegment, b: NodeSlotPathSegment) =>
  a.parent[0] === b.parent[0] && a.name === b.name && a.index === b.index;

const isUnambiguousSlotPath = (subject: NodeSlotPath, candidate: NodeSlotPath) => {
  const firstDivergingSegmentIndex = subject.findIndex(
    (segment, index) => !candidate[index] || !segmentsAreEqual(segment, candidate[index]),
  );
  const followingSegments = subject.slice(firstDivergingSegmentIndex);
  // for non lineage section of path we can only be sure if not multi-value slot
  return !followingSegments.find((segment) => "index" in segment);
};
